import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import { RefreshCw, Sparkles, CheckCircle2, Download } from 'lucide-react'
import { exportMrpPlanningToExcel } from './utils/mrpExcel'
import { AuthContext } from '@/features/auth/AuthContext'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageLoading, PageError } from '@/components/ui/PageAsyncState'
import { getMrpAdapter } from './adapters/mrpAdapter'
import { MrpKpiStrip } from './components/MrpKpiStrip'
import { MrpFiltersBar } from './components/MrpFiltersBar'
import { MrpPlanningTable } from './components/MrpPlanningTable'
import { MrpTraceDrawer } from './components/MrpTraceDrawer'
import type {
  MrpDashboardFilter,
  MrpPlanningRow,
  MrpPlanningViewModel,
} from './types/mrpTypes'

const DEFAULT_FILTER: MrpDashboardFilter = {
  search: '',
  shortage_only: false,
  demand_type: 'ALL',
  issue_filter: 'ALL',
}

export function MrpPlanningDashboardPage(): React.ReactElement {
  const adapter = getMrpAdapter()
  const authCtx = useContext(AuthContext)
  const canWritePo = authCtx ? authCtx.can('po.write') : true

  // State
  const [data, setData] = useState<MrpPlanningViewModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<MrpDashboardFilter>(DEFAULT_FILTER)
  const [selectedTraceRow, setSelectedTraceRow] = useState<MrpPlanningRow | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => {
      setToastMessage(null)
    }, 4000)
  }, [])

  // Load view model from adapter (no client-side math)
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const vm = await adapter.fetchPlanningView({ demand_type: filter.demand_type })
      setData(vm)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Lỗi khi tải bảng cân đối MRP'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [adapter, filter.demand_type])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Client-side filtering of presentation rows (does NOT alter raw server values)
  const filteredRows = useMemo(() => {
    if (!data?.rows) return []

    return data.rows.filter((row) => {
      // 1. Search filter
      if (filter.search.trim()) {
        const q = filter.search.trim().toLowerCase()
        const nameMatch = row.material_name?.toLowerCase().includes(q)
        const codeMatch = row.material_code?.toLowerCase().includes(q)
        const idMatch = row.material_id?.toLowerCase().includes(q)
        const specMatch =
          typeof row.material_spec === 'string'
            ? row.material_spec.toLowerCase().includes(q)
            : JSON.stringify(row.material_spec || {}).toLowerCase().includes(q)
        if (!nameMatch && !codeMatch && !idMatch && !specMatch) return false
      }

      // 2. Shortage only filter
      if (filter.shortage_only && row.shortage_qty <= 0) {
        return false
      }

      // 3. Demand type filter (if rows have specific demand trace)
      if (filter.demand_type !== 'ALL') {
        const matchesDemandType = row.demand_traces?.some(
          (t) => t.demand_type?.toUpperCase() === filter.demand_type.toUpperCase(),
        )
        if (!matchesDemandType && row.demand_traces?.length > 0) {
          return false
        }
      }

      // 4. Issue filter
      if (filter.issue_filter === 'ISSUES_ONLY' && (!row.issues || row.issues.length === 0)) {
        return false
      }
      if (filter.issue_filter === 'NORMAL_ONLY' && row.issues && row.issues.length > 0) {
        return false
      }

      return true
    })
  }, [data?.rows, filter])

  // Sync suggestions handler
  const handleSyncSuggestions = async () => {
    if (syncing) return
    if (!canWritePo) {
      showToast('Bạn không có quyền cập nhật đề xuất mua hàng (yêu cầu quyền po.write).')
      return
    }
    if (!adapter.syncMrpSuggestions) {
      showToast('Chức năng đồng bộ không khả dụng.')
      return
    }

    setSyncing(true)
    try {
      const res = await adapter.syncMrpSuggestions()
      const dismissedMsg = res.dismissed != null && res.dismissed > 0 ? `, ${res.dismissed} đã hủy` : ''
      showToast(`Đã đồng bộ đề xuất mua (MRP): ${res.created} tạo mới, ${res.updated} cập nhật${dismissedMsg}.`)
      await loadData()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Lỗi đồng bộ đề xuất mua hàng'
      showToast(msg)
    } finally {
      setSyncing(false)
    }
  }

  const allRowsCount = data?.rows?.length ?? 0
  const isZeroShortage = allRowsCount > 0 && data?.summary?.shortage_count === 0

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-5 right-5 z-50 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-lg animate-fade-in">
          {toastMessage}
        </div>
      )}

      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kế hoạch', to: '/planning' },
          { label: 'Cân đối vật tư (MRP)' },
        ]}
      />

      {/* Page Header */}
      <PageHeader
        title="Cân đối vật tư (MRP)"
        subtitle="Bảng cân đối nhu cầu kế hoạch sản xuất, tồn khả dụng và đề xuất mua hàng"
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSyncSuggestions}
              disabled={syncing || !canWritePo}
              title={
                !canWritePo
                  ? 'Bạn không có quyền cập nhật đề xuất mua hàng (yêu cầu quyền po.write)'
                  : 'Đồng bộ kết quả thiếu hụt vào danh sách Đề xuất mua hàng'
              }
              className="gap-1.5"
            >
              {syncing ? (
                <RefreshCw className="h-3.5 w-3.5 text-blue-600 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 text-blue-600" />
              )}
              {syncing ? 'Đang đồng bộ…' : 'Đề xuất mua (MRP)'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={loadData}
              disabled={loading}
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => exportMrpPlanningToExcel(filteredRows)}
              disabled={loading || filteredRows.length === 0}
              className="gap-1.5"
              title="Xuất bảng cân đối vật tư ra file Excel (.xlsx)"
            >
              <Download className="h-3.5 w-3.5" />
              Xuất Excel
            </Button>
          </div>
        }
      />

      {/* Loading state */}
      {loading && <PageLoading label="Đang tải dữ liệu cân đối MRP…" />}

      {/* Error state */}
      {error && !loading && (
        <PageError message={error} onRetry={() => void loadData()} />
      )}

      {/* Data populated */}
      {!loading && !error && data && (
        <>
          {/* KPI Strip */}
          <MrpKpiStrip summary={data.summary} />

          {/* Zero Shortage Banner if applicable */}
          {isZeroShortage && (
            <div
              data-testid="zero-shortage-banner"
              className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-900"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>
                <strong>Tất cả vật tư đều đủ hàng:</strong> Toàn bộ nhu cầu sản xuất trong kỳ đã
                được đáp ứng bởi tồn kho khả dụng hoặc các đơn PO đang về. Không có dòng thiếu hụt.
              </span>
            </div>
          )}

          {/* Filters Bar */}
          <MrpFiltersBar
            filter={filter}
            onChange={setFilter}
            onReset={() => setFilter(DEFAULT_FILTER)}
          />

          {/* Empty State */}
          {filteredRows.length === 0 ? (
            <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-8">
              <EmptyState
                title={
                  allRowsCount === 0
                    ? 'Không có dữ liệu kế hoạch MRP'
                    : 'Không tìm thấy dòng vật tư phù hợp bộ lọc'
                }
                description={
                  allRowsCount === 0
                    ? 'Chưa có nhu cầu vật tư từ lệnh sản xuất được xác nhận.'
                    : 'Vui lòng thay đổi từ khóa tìm kiếm hoặc điều chỉnh điều kiện lọc.'
                }
                action={
                  allRowsCount > 0 ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setFilter(DEFAULT_FILTER)}
                    >
                      Xóa bộ lọc
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            /* Populated Table */
            <MrpPlanningTable
              rows={filteredRows}
              onOpenTrace={(row) => setSelectedTraceRow(row)}
            />
          )}
        </>
      )}

      {/* Traceability Drawer */}
      <MrpTraceDrawer
        open={Boolean(selectedTraceRow)}
        onClose={() => setSelectedTraceRow(null)}
        row={selectedTraceRow}
      />
    </div>
  )
}
